import React, { useState } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import { AdminAccountViewModel } from "../../../api/generated/models/AdminAccountViewModel";
import { AdminPermission } from "../../../api/generated/models/AdminPermission";
import { AdminAccountsService } from "../../../api/generated/services/AdminAccountsService";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

interface DeleteAdminModalProps {
  isOpen: boolean;
  admin: AdminAccountViewModel | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DeleteAdminModal: React.FC<DeleteAdminModalProps> = ({
  isOpen,
  admin,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if the admin being deleted is a SuperAdmin
  const isSuperAdmin =
    admin?.adminPermissions?.includes(AdminPermission.SUPER_ADMIN) ?? false;

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleConfirm = async () => {
    // Prevent removing SuperAdmin
    if (isSuperAdmin) {
      setError("Super Admins cannot be removed from the dashboard");
      return;
    }

    if (!admin?.id) {
      setError("Admin ID is missing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await AdminAccountsService.postApiAdminAccountsDeleteAdminAccount({
        adminAccountId: admin.id,
      });
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error removing admin account:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to remove admin account. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!admin) {
    return null;
  }

  return (
    <Modal
      className="modal-content"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Remove Admin"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Remove Admin</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <div className="space-y-4">
        {isSuperAdmin ? (
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
            <p className="text-sm text-orange-800">
              <span className="font-medium">{admin.email}</span> is a Super
              Admin.
            </p>
            <p className="text-sm text-orange-800 mt-2 font-medium">
              Super Admins cannot be removed from the dashboard. Please contact
              support if you need to remove this account.
            </p>
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <p className="text-sm text-red-800">
              Are you sure you want to remove the admin account for{" "}
              <span className="font-medium">{admin.email}</span>?
            </p>
            <p className="text-sm text-red-800 mt-2 font-medium">
              This action cannot be undone.
            </p>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={loading}
            disabled={isSuperAdmin}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Remove Admin
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteAdminModal;
